import React, { useState, useEffect } from 'react'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import ConfirmDialog from '../ConfirmDialog'
import { HistoryMobileDateRangePicker } from '../HistoryDateRangePickers'
import MobilePaginationActions from './MobilePaginationActions'

const MobileHistoryTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [historyList, setHistoryList] = useState([])
    const [allHistoryData, setAllHistoryData] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs().subtract(7, 'day'))
    const [endDate, setEndDate] = useState(() => dayjs())
    const [showFilters, setShowFilters] = useState(false)
    const [ipFilter, setIpFilter] = useState('')
    const [noteFilter, setNoteFilter] = useState('')

    //history purchase
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState([])
    const [afterBuy, setAfterBuy] = useState(false)
    const [errorGetAgentInfo, setErrorGetAgentInfo] = useState(false)
    const [loadingRowId, setLoadingRowId] = useState(null)
    
    //notes
    const [notes, setNotes] = useState({})

    // CSS стили для анимации спиннера
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Анимация спиннера */
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - historyList?.expiredRentals?.length) : 0

    const handleChangePage = React.useCallback((event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }, [])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])

    // Загрузка данных с сервера
    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')
            const historyResponse = await props.apiClient.setToken(getAccessTokenSilently()).getUserHistory(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setAllHistoryData(historyResponse)
          } catch (e) {
          }
      }
      callAsync()   
    },[startDate, endDate, page, rowsPerPage])

    // Функция для фильтрации данных по IP и запискам
    const filterHistoryByIpAndNotes = React.useCallback((data, ipFilter, noteFilter, notesData) => {
      if (!data?.expiredRentals) {
        return data
      }
      
      let filteredItems = data.expiredRentals
      
      // Фильтрация по IP
      if (ipFilter && ipFilter.trim() !== '') {
        filteredItems = filteredItems.filter(item => 
          item.ip && item.ip.toLowerCase().includes(ipFilter.toLowerCase())
        )
      }
      
      // Фильтрация по запискам
      if (noteFilter && noteFilter.trim() !== '') {
        filteredItems = filteredItems.filter(item => {
          const note = notesData[item.proxyid] || item.note || ''
          return note.toLowerCase().includes(noteFilter.toLowerCase())
        })
      }
      
      return {
        ...data,
        expiredRentals: filteredItems,
        total: filteredItems.length
      }
    }, [])

    // Применение фильтрации по IP и запискам
    useEffect(() => {
      const filteredData = filterHistoryByIpAndNotes(allHistoryData, ipFilter, noteFilter, notes)
      setHistoryList(filteredData)
    }, [allHistoryData, ipFilter, noteFilter, notes, filterHistoryByIpAndNotes])

    const handleDateRangeChange = React.useCallback((start, end) => {
        setStartDate(start)
        setEndDate(end)
    }, [])

    const handleIpFilterChange = React.useCallback((event) => {
      setIpFilter(event.target.value)
      setPage(0) // Сброс на первую страницу при изменении фильтра
    }, [])

    const handleNoteFilterChange = React.useCallback((event) => {
      setNoteFilter(event.target.value)
      setPage(0) // Сброс на первую страницу при изменении фильтра
    }, [])

    const handleButtonClick = React.useCallback(async(row) => {
       try {
          setLoadingRowId(row?.proxyid)
          const agentData = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(row?.proxyid)
          if(agentData?.proxyid == null || agentData?.proxyid == undefined){
              throw 'error'
          }

          setSelectedRow(agentData)
          setConfirmOpen(true)
       } catch(e){
          setErrorGetAgentInfo(true)
       } finally {
          setLoadingRowId(null)
       }
    }, [])

    const handleCloseGetAgentInfoError = React.useCallback(() => {
      setErrorGetAgentInfo(false)
    }, [])

    useEffect(() => {
      if (errorGetAgentInfo) {
        const timer = setTimeout(() => setErrorGetAgentInfo(false), 4000)
        return () => clearTimeout(timer)
      }
    }, [errorGetAgentInfo])


    return (
        <div style={{
            width: '100%',
            height: '100%',
            padding: 'var(--spacing-sm)',
            overflow: 'auto'
        }}>
            
            {/* Мобильные фильтры по дате */}
            <div className="mobile-filters">
                <div 
                    className="mobile-filters-header"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <span className="mobile-filters-title">Date Filters</span>
                    <span style={{ 
                        color: 'var(--text-muted)',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {showFilters && (
                    <div className="mobile-filters-content">
                        <div className="mobile-filters-grid" style={{ gridTemplateColumns: '1fr' }}>
                            <HistoryMobileDateRangePicker
                                label="Date range"
                                startDate={startDate}
                                endDate={endDate}
                                onRangeChange={handleDateRangeChange}
                            />
                        </div>
                        <div className="mobile-filter-group" style={{ marginTop: 'var(--spacing-md)' }}>
                            <label className="mobile-filter-label">
                                Search by IP
                                {ipFilter && (
                                    <span style={{
                                        marginLeft: '8px',
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--primary-orange)',
                                        fontWeight: '500'
                                    }}>
                                        ({historyList?.total || 0} found)
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                className={`mobile-filter-input ${ipFilter ? 'active' : ''}`}
                                placeholder="Enter IP address..."
                                value={ipFilter}
                                onChange={handleIpFilterChange}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: ipFilter ? '1px solid var(--primary-orange)' : '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '12px 16px',
                                    fontSize: 'var(--text-sm)',
                                    transition: 'var(--transition-normal)',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 152, 0, 0.2)'
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = ipFilter ? 'var(--primary-orange)' : 'var(--light-gray)'
                                    e.target.style.boxShadow = 'none'
                                }}
                            />
                        </div>
                        <div className="mobile-filter-group" style={{ marginTop: 'var(--spacing-md)' }}>
                            <label className="mobile-filter-label">
                                Search by Notes
                                {noteFilter && (
                                    <span style={{
                                        marginLeft: '8px',
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--primary-orange)',
                                        fontWeight: '500'
                                    }}>
                                        ({historyList?.total || 0} found)
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                className={`mobile-filter-input ${noteFilter ? 'active' : ''}`}
                                placeholder="Enter note text..."
                                value={noteFilter}
                                onChange={handleNoteFilterChange}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: noteFilter ? '1px solid var(--primary-orange)' : '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '12px 16px',
                                    fontSize: 'var(--text-sm)',
                                    transition: 'var(--transition-normal)',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 152, 0, 0.2)'
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = noteFilter ? 'var(--primary-orange)' : 'var(--light-gray)'
                                    e.target.style.boxShadow = 'none'
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {historyList?.expiredRentals?.length > 0 ? (
                <div>
                    {/* Мобильные карточки истории */}
                    <div style={{ padding: '0' }}>
                        {historyList?.expiredRentals?.map((row, index) => (
                            <div
                                key={index}
                                style={{
                                    background: 'var(--medium-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-md)',
                                    padding: 'var(--spacing-md)',
                                    marginBottom: 'var(--spacing-sm)',
                                    cursor: 'pointer',
                                    transition: 'var(--transition-normal)'
                                }}
                            >
                                {/* Заголовок карточки */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: 'var(--spacing-md)'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-lg)',
                                            fontWeight: '600',
                                            color: 'var(--text-light)',
                                            marginBottom: 'var(--spacing-xs)'
                                        }}>
                                            {row?.ip}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-muted)'
                                        }}>
                                            {row?.geo?.cc} • {row?.geo?.city}, {row?.geo?.reg}
                                        </div>
                                    </div>
                                    <button 
                                        className="btn btn-primary btn-small"
                                        onClick={() => handleButtonClick(row)}
                                        disabled={loadingRowId === row?.proxyid}
                                        style={{ 
                                            minWidth: '60px',
                                            opacity: loadingRowId === row?.proxyid ? 0.7 : 1,
                                            cursor: loadingRowId === row?.proxyid ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {loadingRowId === row?.proxyid ? (
                                            <div style={{
                                                width: '14px',
                                                height: '14px',
                                                border: '2px solid var(--light-gray)',
                                                borderTop: '2px solid var(--primary-orange)',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }} />
                                        ) : (
                                            'Buy'
                                        )}
                                    </button>
                                </div>

                                {/* Детали истории */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 'var(--spacing-md)',
                                    marginBottom: 'var(--spacing-md)'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: '4px',
                                            fontWeight: '500'
                                        }}>
                                            ZIP Code
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-light)'
                                        }}>
                                            {row?.geo?.zip || 'N/A'}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: '4px',
                                            fontWeight: '500'
                                        }}>
                                            ISP
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-light)',
                                            wordBreak: 'break-word'
                                        }}>
                                            {row?.geo?.isp?.replace(/(.{15})..+/, "$1…")}
                                        </div>
                                    </div>
                                </div>

                                {/* Дата */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: 'var(--spacing-sm)',
                                    background: 'var(--dark-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--light-gray)'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: '2px'
                                        }}>
                                            Purchased
                                        </div>
                                        <div className="shop-accent-text" style={{
                                            fontSize: 'var(--text-sm)',
                                            display: 'inline-block',
                                        }}>
                                            {moment(row.expiredAt).format('MMM DD, YYYY')}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--text-muted)',
                                        textAlign: 'right'
                                    }}>
                                        <div>{moment(row.expiredAt).format('h:mm A')}</div>
                                    </div>
                                </div>

                                {/* Заметка */}
                                <div style={{
                                    marginTop: 'var(--spacing-sm)',
                                    padding: 'var(--spacing-sm)',
                                    background: 'var(--dark-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--light-gray)'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: 'var(--spacing-xs)'
                                    }}>
                                        <span style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            fontWeight: '500'
                                        }}>
                                            📝 Note
                                        </span>
                                    </div>
                                    <div 
                                        style={{
                                            fontSize: 'var(--text-sm)',
                                            color: notes[row?.proxyid] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                                            fontStyle: notes[row?.proxyid] || row?.note ? 'normal' : 'italic',
                                            lineHeight: '1.4',
                                            cursor: (notes[row?.proxyid] || row?.note) ? 'help' : 'default'
                                        }}
                                        title={(notes[row?.proxyid] || row?.note || 'No note added').replace(/(.{20})/g, '$1\n')}
                                    >
                                        {(notes[row?.proxyid] || row?.note || 'No note added').length > 10 
                                            ? (notes[row?.proxyid] || row?.note || 'No note added').substring(0, 10) + '...'
                                            : (notes[row?.proxyid] || row?.note || 'No note added')
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Мобильная пагинация */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Строка с навигацией */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <MobilePaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={historyList?.total || 0}
                                onPageChange={handleChangePage}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3 className="empty-state-title">No history records</h3>
                    <p className="empty-state-description">
                        You don't have any history records for the selected period.
                    </p>
                </div>
            )}
            
            {/* Диалог подтверждения покупки */}
            <ConfirmDialog 
                open={confirmOpen} 
                setConfirmOpen={setConfirmOpen} 
                selectedProxy={selectedRow} 
                apiClient={props.apiClient} 
                setAfterBuy={setAfterBuy}
                refreshUserData={props.refreshUserData}
            />
            
            {/* Уведомление об ошибке */}
            {errorGetAgentInfo && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#FF9800',
                    color: 'var(--white)',
                    padding: '12px 24px',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-card)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    animation: 'fadeInDown 0.3s ease-out'
                }}>
                    <span>⚠️</span>
                    <span>Couldn't get proxy info. Please try again later.</span>
                    <button
                        onClick={handleCloseGetAgentInfoError}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: 'var(--text-lg)',
                            marginLeft: 'var(--spacing-sm)'
                        }}
                    >
                        ×
                    </button>
                    <style>{`
                        @keyframes fadeInDown {
                            from {
                                opacity: 0;
                                transform: translateX(-50%) translateY(-20px);
                            }
                            to {
                                opacity: 1;
                                transform: translateX(-50%) translateY(0);
                            }
                        }
                    `}</style>
                </div>
            )}
        </div>
    )
}

export default MobileHistoryTable
